import React, { useState, useEffect, useContext, useRef, useCallback, useMemo } from "react";
import { Gantt } from "wx-react-gantt";
import { Willow } from "wx-react-gantt";
import { UrlContext } from "../../contexts/UrlContext";
import "wx-react-gantt/dist/gantt.css";
import styled from "styled-components";
import LoadingIcon from "../../images/Loading_icon.gif";
import { motion, AnimatePresence } from "framer-motion";
import { AppContext } from "../../contexts/AppContext";
import "../../styles/accountdetails.css";


// ==================== STYLED COMPONENTS ====================
const Container = styled.div`
  padding: 24px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 10px 5px rgba(0, 0, 0, 0.05);
`;

const Header = styled.h2`
  font-size: 1.5rem;
  color: #2c3e50;
  margin-bottom: 20px;
  font-weight: 600;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 40px;
`;

const ErrorMessage = styled.div`
  color: #e74c3c;
  background: #fde8e8;
  padding: 12px;
  border-radius: 4px;
  margin: 20px 0;
`;

const FiltersContainer = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px; /* Controls spacing between buttons */
  flex-wrap: wrap;
`;

const FiltersWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const ActionButtonScales = styled.button`
  padding: 8px 16px;
  background-color:rgb(142, 142, 145);
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  margin-bottom: 20px;

  &:hover {
    background-color:rgb(62, 62, 62);
  }

  &:focus {
    outline: none;
    box-shadow: none;
  }
`

const ActionButton = styled.button`
  padding: 8px 16px;
  background-color: rgba(54, 85, 97, 1);
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;

  &:hover {
    background-color: rgb(60, 93, 107);
  }

    &:disabled {
    background-color: rgba(54, 85, 97, 0.5); // Faded color for disabled
    cursor: not-allowed;
  }

  &:focus {
    outline: none;
    box-shadow: none;
  }
`

const FilterInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 14px;
  width: 200px;
  &:focus {
    outline: none;
    border-color: #6366f1;
  }
`;

const FilterSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 14px;
  width: 200px;
  &:focus {
    outline: none;
    border-color: #6366f1;
  }
`;

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContainer = styled(motion.div)`
  background: white;
  padding: 24px;
  border-radius: 12px;
  width: 400px;
  max-width: 90%;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  position: relative;
`;

const ModalHeader = styled.h3`
  margin-top: 0;
  margin-bottom: 16px;
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 8px;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const Label = styled.span`
  font-weight: 500;
  color: #4b5563;
`;

const Value = styled.span`
  font-weight: 400;
  color: #111827;
`;

const CloseButton = styled.button`
  margin-top: 20px;
  margin-left: 150px;
  padding: 10px 16px;
  background-color:rgba(54, 85, 97, 1);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  transition: background 0.2s ease-in-out;

  &:hover {
    background-color:rgb(60, 93, 107);
  }
`;

// ==================== UTILS ====================
const calculateProgress = (start, end) => {
    const today = new Date();
    const startDate = new Date(start);
    const endDate = end ? new Date(end) : today;

    if (today < startDate) return 0;
    if (today > endDate) return 100;

    const totalDuration = endDate.getTime() - startDate.getTime();
    const elapsed = today.getTime() - startDate.getTime();
    return Math.min(100, Math.max(0, (elapsed / totalDuration) * 100));
};

const calculateWorkingDays = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);

    if (start > end) return 0;

    let count = 0;
    let current = new Date(start);

    while (current <= end) {
        const day = current.getDay();
        if (day !== 0 && day !== 6) {
            count++;
        }
        current.setDate(current.getDate() + 1);
    }

    return count;
};

// Custom debounce hook
const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => clearTimeout(handler);
    }, [value, delay]);

    return debouncedValue;
};

// ==================== MAIN COMPONENT ====================
const ProjectGantt = ({ projectID = "" }) => {
    // ==================== STATE ====================
    const {
        owners,
    } = useContext(AppContext);
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { API_URL } = useContext(UrlContext);
    const ganttRef = useRef(null);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [zoomScale, setZoomScale] = useState(null);
    const scrollRef = useRef(null); // reference to the scrollable container
    const [scrollLeft, setScrollLeft] = useState(0);

    // Filter inputs
    const [clientFilter, setClientFilter] = useState("");
    const [zohoIDFilter, setZohoIDFilter] = useState("");
    const [projectNameFilter, setProjectNameFilter] = useState("");
    const [ownerFilter, setOwnerFilter] = useState("");

    // Filter states
    const [filters, setFilters] = useState({
        client: "",
        zohoID: "",
        projectName: "",
        owner: ""
    });

    // Debounced filters
    const debouncedFilters = useDebounce(filters, 300);

    const [selectedTask, setSelectedTask] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const PAGE_SIZE = projectID ? 10: 20;
    const [fetchedPages, setFetchedPages] = useState([]);


    const links = [{ id: 1, source: 20, target: 21, type: "e2e" }];

    // const scales = [
    //     { unit: "month", step: 1, format: "MMMM yyy" },
    // ];

    const dayScale = [{ unit: 'day', step: 1, format: 'd-MMM-yy' }];
    const weekScale = [{ unit: 'month', step: 1, format: 'MMM yyy' },{ unit: 'week', step: 1, format: 'w' }];
    const monthScale = [{ unit: 'month', step: 1, format: 'MMM yyy' }];
    const yearScale = [{ unit: 'year', step: 1, format: 'yyyy' }];

    const [newScales, setNewScales] = useState(dayScale);

    const columns = [
        { id: "client", header: "Client", flexgrow: 1 },
        { id: "zohoID", header: "Zoho ID", flexgrow: 1 },
        { id: "projectName", header: "Project Name", flexgrow: 1 },
        { id: "text", header: "Name", flexgrow: 2 },
    ];

    const markers = [
        {
            start: new Date(),
            text: "Today",
            // css: "myMiddleClass"
        },
    ];

    useEffect(() => {
        if (ganttRef.current) {
            const styleString = document.querySelectorAll(".wx-marker")[0].attributes[1].textContent;
            const todayMarker = parseInt(styleString.match(/\d+/)[0], 10) - 500;
            if (ganttRef.current && tasks.length > 0) {
                // Scroll to the left: value in pixels (e.g., 1000)
                ganttRef.current.exec("scroll-chart", {
                    left: todayMarker,
                });
            }


        }
    }, [tasks]);




    // ==================== DATA FETCHING ====================
    const fetchData = useCallback(async () => {
        if (!fetchedPages.includes(page) && projectID) {
            setLoading(true);
            setError(null);
            try {
                const token = JSON.parse(localStorage.getItem("eph-token"))?.value;
                if (!token) throw new Error("No auth token found");

                const queryParams = new URLSearchParams({
                    page,
                    pageSize: PAGE_SIZE,
                    ...debouncedFilters
                }).toString();

                // const response = await fetch(`${API_URL}project-gantt/${projectID}`, {
                //     headers: { "x-access-token": token },
                // });

                const URL = `${API_URL}project-gantt/${projectID}?page=${page}&pageSize=${PAGE_SIZE}`
                await fetch(
                    URL
                ).then((res) => res.json())
                    .then((data) => {
                        console.log("Fetched tasks:", data);
                        // const { data } = await response.json();
                        const newTasks = transformData(data.data);
                        setTasks((prevTasks) => [...prevTasks, ...newTasks]);
                        const total = data.pagination?.total || 0;
                        const totalPages = Math.ceil(total / PAGE_SIZE);
                        setTotalPages(totalPages);
                        setFetchedPages((prev) => [...prev, page]);
                        setHasMore(page < totalPages);
                    })
                // if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);


            } catch (err) {
                setError(err.message);
                console.error("Fetch error:", err);
            } finally {
                setLoading(false);
            }
        } else if (!projectID){

            setLoading(true);
            setError(null);
            try {
                const token = JSON.parse(localStorage.getItem("eph-token"))?.value;
                if (!token) throw new Error("No auth token found");

                const queryParams = new URLSearchParams({
                    page,
                    pageSize: PAGE_SIZE,
                    ...debouncedFilters
                }).toString();

                // const response = await fetch(`${API_URL}project-gantt/${projectID}`, {
                //     headers: { "x-access-token": token },
                // });

                const URL = `${API_URL}project-progress-gantt?${queryParams}`
                await fetch(
                    URL
                ).then((res) => res.json())
                    .then((data) => {
                        console.log("Fetched tasks:", data);
                        // const { data } = await response.json();
                        const newTasks = transformData(data.data);
                        setTasks(page === 1 ? newTasks : [...tasks, ...newTasks]);
                        const total = data.pagination?.total || 0;
                        const totalPages = Math.ceil(total / PAGE_SIZE);
                        setTotalPages(totalPages);
                        setFetchedPages((prev) => [...prev, page]);
                        setHasMore(page < totalPages);
                    })
                // if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);


            } catch (err) {
                setError(err.message);
                console.error("Fetch error:", err);
            } finally {
                setLoading(false);
            }

        }

    }, [API_URL, projectID, page, debouncedFilters]);

    const handleLoadMore = () => {
        setPage((prev) => prev + 1);
    };

    // Handle filter changes
    const handleFilterChange = (name, value) => {
        console.log("Filter changed:", name, value, filters);
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    // Reset to page 1 when filters change
    useEffect(() => {
        setPage(1);
    }, [debouncedFilters]);

    // ==================== DATA TRANSFORMATION ====================
    const transformData = (projects) => {
        const allStartDates = projects.flatMap(project =>
            project.engagements.map(engagement => new Date(engagement.startDate))
        );

        const minStartDate = new Date(Math.min(...allStartDates.map(date => date.getTime())));

        const calculatedEndDate = new Date();
        calculatedEndDate.setMonth(calculatedEndDate.getMonth() + 2);

        setStartDate(minStartDate);
        setEndDate(calculatedEndDate);

        const tasks = projects.flatMap((project, projectIndex) =>
            project.engagements.map((engagement, engagementIndex) => ({
                id: `${projectIndex}-${engagementIndex}`,
                text: engagement.name || `Engagement ${engagementIndex + 1}`,
                client: engagement.client || `Client ${engagementIndex + 1}`,
                owner: engagement.owner || `Owner ${engagementIndex + 1}`,
                zohoID: engagement.zohoID || `Zoho ID ${engagementIndex + 1}`,
                projectName: project.projectName || `Project ${projectIndex + 1}`,
                start: new Date(engagement.startDate),
                end: engagement.endDate && engagement.startDate !== engagement.endDate ? new Date(engagement.endDate) : null,
                type: engagement.projectType === "exp" ? "milestone" : "task",
                progress: calculateProgress(engagement.startDate, engagement.endDate),
                duration: engagement.startDate !== engagement.endDate ? calculateWorkingDays(engagement.startDate, engagement.endDate) : 1,
                styles: {
                    progressColor: "#6366f1",
                    progressSelectedColor: "#4f46e5",
                    backgroundColor: "#e0e7ff",
                    backgroundSelectedColor: "#c7d2fe",
                },
                isDisabled: false,
            }))
        );

        return tasks;
    };

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // ==================== FILTERING ====================


    // Memoized filtered tasks
    const filteredTasks = useMemo(() =>
        tasks.filter(task => {
            const matchesClient = task.client?.toLowerCase().includes(clientFilter.toLowerCase());
            const matchesZohoID = task.zohoID?.toLowerCase().includes(zohoIDFilter.toLowerCase());
            const matchesProject = task.projectName?.toLowerCase().includes(projectNameFilter.toLowerCase());
            const matchesOwner = ownerFilter === "" || task.owner === ownerFilter;
            return matchesClient && matchesZohoID && matchesProject && matchesOwner;
        }),
        [tasks, clientFilter, zohoIDFilter, projectNameFilter, ownerFilter]
    );

    // Memoized paginated tasks (will update when page changes)
    const paginatedTasks = useMemo(() =>
        filteredTasks.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
        [filteredTasks, page]
    );


    useEffect(() => {
        const interval = setInterval(() => {
            const marker = document.querySelector('.wx-marker');
            if (marker) {
                const left = parseInt(marker.style.left, 10) || 0;
                marker.style.left = `${left + 50}px`;
                clearInterval(interval);
            }
        }, 100);

        return () => clearInterval(interval);
    }, [paginatedTasks, filteredTasks, newScales]);

    function init(api) {
        ganttRef.current = api;
        let scrollLeftVar = 0;


        api.on("select-task", (ev) => {
            setScrollLeft(api.getState().scrollLeft);
            setZoomScale(api.getState().zoom);
            const task = api.getState()._tasks.find(t => t.id === ev.id);
            if (task) {
                setSelectedTask(task);
                setIsModalOpen(true);
            }
        });

    }

    const closeModal = () => {

        setIsModalOpen(false)
        setSelectedTask(null);
    }

    // ==================== RENDER ====================
    return (
        <Container>
            <Header>Project Timeline</Header>

            {error && <ErrorMessage>{error}</ErrorMessage>}

            {loading ? (
                <LoadingContainer>
                    <img src={LoadingIcon} alt="Loading..." style={{ height: 50 }} />
                </LoadingContainer>
            ) : tasks.length > 0 ? (
                <>
                    <FiltersWrapper>
                        <FiltersContainer>
                            <FilterInput
                                type="text"
                                placeholder="Filter by Client"
                                value={projectID ? clientFilter : filters.client}
                                onChange={(e) => {
                                    if (projectID) {
                                        setClientFilter(e.target.value);
                                    } else {
                                        handleFilterChange("client", e.target.value);
                                    }
                                }}
                            />
                            <FilterInput
                                type="text"
                                placeholder="Filter by Zoho ID"
                                value={projectID ? zohoIDFilter : filters.zohoID}
                                onChange={(e) => projectID ? setZohoIDFilter(e.target.value) : handleFilterChange("zohoID", e.target.value)}
                            />
                            <FilterInput
                                type="text"
                                placeholder="Filter by Project Name"
                                value={projectID ? projectNameFilter : filters.projectName}
                                onChange={(e) => projectID ? setProjectNameFilter(e.target.value) : handleFilterChange("projectName", e.target.value)}
                            />
                            <FilterSelect
                                value={projectID ? ownerFilter : filters.owner}
                                onChange={(e) => projectID ? setOwnerFilter(e.target.value) : handleFilterChange("owner", e.target.value)}
                            >
                                <option value="">All Owners</option>
                                {owners?.map((owner) => (
                                    <option key={owner.fullname} value={owner.fullname}>
                                        {owner.username}
                                    </option>
                                ))}
                            </FilterSelect>
                        </FiltersContainer>

                        <ButtonGroup>
                            <ActionButtonScales onClick={() => {
                                setNewScales(dayScale);
                            }}>
                                Day
                            </ActionButtonScales>
                            <ActionButtonScales onClick={() => {
                                setNewScales(weekScale);
                            }}>
                                Week
                            </ActionButtonScales>
                            <ActionButtonScales onClick={() => {
                                setNewScales(monthScale);
                            }}>
                                Month
                            </ActionButtonScales>
                            <ActionButtonScales onClick={() => {
                                setNewScales(yearScale);
                            }}>
                                Year
                            </ActionButtonScales>
                            <ActionButton style={{ marginBottom: '20px' }} onClick={() => {
                                if (ganttRef.current) {
                                    const styleString = document.querySelectorAll(".wx-marker")[0].attributes[1].textContent;
                                    const todayMarker = parseInt(styleString.match(/\d+/)[0], 10) - 200;
                                    if (ganttRef.current && tasks.length > 0) {
                                        // Scroll to the left: value in pixels (e.g., 1000)
                                        ganttRef.current.exec("scroll-chart", {
                                            left: todayMarker,
                                        });
                                    }
                                }
                            }}>
                                Jump to Today
                            </ActionButton>

                        </ButtonGroup>

                    </FiltersWrapper>

                    <div ref={ganttRef}>
                        <Willow>
                            <Gantt
                                init={init}
                                tasks={paginatedTasks}
                                links={links}
                                scales={newScales}
                                columns={columns}
                                readonly={true}
                                markers={markers}
                                start={startDate}
                                end={endDate}
                                zoom={true}
                                lengthUnit={"hour"}
                            />
                        </Willow>
                    </div>
                </>
            ) : (
                <div>No tasks found</div>
            )}
            {/* Pagination */}
            <div style={{ marginBottom: "12px", display: "flex", alignItems: "center", gap: "12px", justifyContent: "center", marginTop: "20px" }}>
                <ActionButton
                    disabled={page === 1}
                    onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                >
                    Previous
                </ActionButton>
                <span>
                    Page {page} of {totalPages}
                </span>
                <ActionButton
                    disabled={page === totalPages}
                    onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                >
                    Next
                </ActionButton>
            </div>
            <AnimatePresence>
                {isModalOpen && selectedTask && (
                    <ModalOverlay
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => {
                            closeModal();
                        }}
                    >


                        <ModalContainer
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                        >
                            <ModalHeader>Details</ModalHeader>

                            <DetailRow>
                                <Label>Name:</Label>
                                <Value>{selectedTask.text}</Value>
                            </DetailRow>
                            <DetailRow>
                                <Label>Client:</Label>
                                <Value>{selectedTask.client}</Value>
                            </DetailRow>
                            <DetailRow>
                                <Label>Zoho ID:</Label>
                                <Value>{selectedTask.zohoID}</Value>
                            </DetailRow>
                            <DetailRow>
                                <Label>Project:</Label>
                                <Value>{selectedTask.projectName}</Value>
                            </DetailRow>
                            {selectedTask.type === "task" ? (
                                <>
                                    <DetailRow>
                                        <Label>Start:</Label>
                                        <Value>{selectedTask.start?.toLocaleDateString()}</Value>
                                    </DetailRow>
                                    <DetailRow>
                                        <Label>End:</Label>
                                        <Value>{selectedTask.end ? selectedTask.end.toLocaleDateString() : "N/A"}</Value>
                                    </DetailRow>
                                </>
                            ) : (
                                <DetailRow>
                                    <Label>Date:</Label>
                                    <Value>{selectedTask.start?.toLocaleDateString()}</Value>
                                </DetailRow>
                            )}
                            <DetailRow>
                                <Label>Progress:</Label>
                                <Value>{Math.round(selectedTask.progress)}%</Value>
                            </DetailRow>

                            <CloseButton onClick={() => {
                                closeModal();
                            }}>Close</CloseButton>
                        </ModalContainer>

                    </ModalOverlay>
                )}
            </AnimatePresence>
        </Container>
    );
};

export default ProjectGantt;